<template>
    <td
        role="cell"
        :style="containerStyle"
        :class="containerClass"
        @click="setFocus"
        :data-p-frozen-column="frozen"
        :data-p-editable-column="editableCell"
        ref="cellRef"
    >
        <div class="md-editable-grid-cell-wrapper" @dblclick="onCellDblClick">
            <!-- Editing mode -->
            <div
                v-if="isEditing"
                v-click-outside="closeDropdown"
                class="editable-select-cell-content-wrapper"
            >
                <div
                    :class="{
                        'md-editable-grid-cell-content-invalid': invalid,
                        'md-editable-grid-cell-editing': !invalid
                    }"
                    class="md-editable-grid-cell-content"
                    contenteditable="true"
                    ref="inputRef"
                    :data-placeholder="placeholder"
                    @mouseover="checkOverflow"
                    @blur="finishEdit"
                    @input="onInput"
                    @keydown.down.prevent="navigateOptions(1)"
                    @keydown.up.prevent="navigateOptions(-1)"
                    @keydown="handleKeydown"
                >
                    {{ displayValue }}
                </div>

                <div
                    class="editable-select-cell-icon-container"
                    @mousedown.prevent="toggleDropdown"
                >
                    <i class="ikon med-expand"></i>
                </div>
            </div>

            <!-- Display mode -->
            <div v-else class="editable-select-cell-display-wrapper">
                <span
                    class="md-editable-grid-cell-display"
                    :class="{ 'md-editable-grid-cell-non-editable': !editable }"
                    @dblclick="requestEdit"
                    @keydown="handleDisplayKeydown"
                    tabindex="0"
                    ref="displayRef"
                >
                    {{ valueFormatted }}
                </span>
                <div
                    v-if="editableCell"
                    class="editable-select-cell-display-icon"
                    @mousedown.prevent="requestEdit"
                >
                    <i class="ikon med-expand"></i>
                </div>
            </div>
        </div>
    </td>
</template>

<script>
import ShowMessage from '@/components/messages/ShowMessage.js';
import EditableCellBase from './EditableCellBase.vue';

export default {
    name: 'EditableSelectCell',
    mixins: [ShowMessage],
    inject: ['$parentInstance'],
    extends: EditableCellBase,
    props: {
        value: [String, Number],
        options: { type: Array, default: () => [] },
        placeholder: { type: String, default: 'Select...' },
        optionKey: { type: String, default: 'key' },
        optionValue: { type: String, default: 'value' }
    },
    data() {
        return {
            showDropdown: false,
            searchQuery: '',
            highlightedIndex: -1,
            shouldShowTooltip: false,
            dropdownElement: null,
            dataSelect: {
                code: this.value,
                description: ''
            }
        };
    },
    computed: {
        valueFormatted() {
            return this.formatter ? this.formatter(this.value) : this.displayValue;
        },
        displayValue() {
            const selected = this.options.find(o => o[this.optionKey] === this.value);
            return selected ? selected[this.optionValue] : '';
        },
        searchQueryText() {
            return this.searchQuery.toLowerCase().trim();
        },
        filteredOptions() {
            const query = this.searchQueryText;
            return !query
                ? this.options
                : this.options.filter(opt =>
                      String(opt[this.optionValue])
                          .toLowerCase()
                          .includes(query)
                  );
        },
        showNotMatchLabel() {
            const query = this.searchQueryText;
            return (
                query &&
                !this.options.some(opt =>
                    String(opt[this.optionValue])
                        .toLowerCase()
                        .includes(query)
                )
            );
        }
    },
    watch: {
        showDropdown(val) {
            val ? this.renderDropdown() : this.removeDropdown();
        },
        filteredOptions() {
            if (this.showDropdown) this.renderDropdown();
        },
        highlightedIndex() {
            if (this.showDropdown) this.renderDropdown();
        },
        value(newValue) {
            this.dataSelect = {
                code: newValue,
                description: this.displayValue
            };
        }
    },
    methods: {
        startEdit(char) {
            if (!this.editableCell) {
                this.displayContentFocus();
                return;
            }

            this.isEditing = true;

            if (char !== undefined) {
                this.$nextTick(() => {
                    this.searchQuery = char;
                    this.showDropdown = true;
                    this.highlightedIndex = this.filteredOptions.length > 0 ? 0 : -1;
                    const el = this.$refs.inputRef;
                    if (el) {
                        el.innerText = char;
                        el.focus();
                        this.moveCaretToEnd(el);
                    }
                });
            } else {
                this.$nextTick(() => {
                    this.searchQuery = '';
                    this.highlightedIndex = this.filteredOptions.findIndex(
                        x => x[this.optionKey] === this.dataSelect.code
                    );
                    if (this.showDropdown) {
                        this.renderDropdown();
                    }
                });
                this.inputContentFocus();
            }
        },

        finishEdit() {
            if (this.showDropdown) {
                this.closeDropdown();
            }

            if (this.isEditing) {
                this.processAutoSelection();
                this.isEditing = false;

                const changed = this.value !== this.dataSelect.code;

                if (changed) {
                    let event = this.createEventData(this.dataSelect.code);
                    if (this.isAppendSection) {
                        this.$emit('input', this.dataSelect.code);
                    } else {
                        this.$parentInstance.cellEditComplete(event);
                    }

                    this.validateCell(this.dataSelect.code);

                    if (this.invalid) {
                        this.showMessage(
                            this.$tc('Msg.Error'),
                            this.invalidMessage(this.name),
                            'errorAutoHide'
                        );
                    }
                }
            }
        },

        checkOverflow() {
            const el = this.$refs.inputRef;

            if (el) {
                this.shouldShowTooltip = el.scrollWidth > el.clientWidth + 1;
            }
        },

        onInput(event) {
            this.searchQuery = event.target.innerText;
            this.showDropdown = true;
            if (this.searchQuery.trim().length !== 0) {
                this.highlightedIndex = 0;
            } else {
                this.highlightedIndex = -1;
            }
        },

        handleKeydown(event) {
            if (event.key === 'Enter') {
                if (this.showDropdown && this.filteredOptions[this.highlightedIndex]) {
                    this.selectOption(this.filteredOptions[this.highlightedIndex], false);
                }
            }

            return this._superHandleKeydown(event);
        },

        _superHandleKeydown(event) {
            return EditableCellBase.methods.handleKeydown.call(this, event);
        },

        processAutoSelection() {
            const text = this.$refs.inputRef.innerText.trim().toLowerCase();

            if (text !== '') {
                const exact = this.options.find(o => o[this.optionValue].toLowerCase() === text);

                if (exact) {
                    this.selectOption(exact, false);
                } else if (this.filteredOptions.length > 0) {
                    this.selectOption(this.filteredOptions[0], false);
                } else {
                    this.clearCell();
                }
            } else {
                this.clearCell();
            }
        },

        clearCell() {
            this.$refs.inputRef.innerText = '';
            this.dataSelect = {
                code: '',
                description: ''
            };
            this.validateCell(null);
        },

        closeDropdown() {
            this.showDropdown = false;
            this.highlightedIndex = 0;
        },

        renderDropdown() {
            this.$nextTick(() => {
                if (!this.$refs.cellRef) {
                    return;
                }

                const rect = this.$refs.cellRef.getBoundingClientRect();
                const listHeight = Math.min(this.filteredOptions.length * 40, 200);
                const spaceBelow = window.innerHeight - rect.bottom;
                const positionTop = spaceBelow < listHeight && rect.top > listHeight;

                const top = positionTop
                    ? rect.top + window.scrollY - listHeight
                    : rect.bottom + window.scrollY;

                if (!this.dropdownElement) {
                    this.dropdownElement = document.createElement('div');
                    document.body.appendChild(this.dropdownElement);
                }

                Object.assign(this.dropdownElement.style, {
                    position: 'absolute',
                    top: `${top}px`,
                    left: `${rect.left + window.scrollX}px`,
                    width: `${rect.width}px`,
                    zIndex: '999999',
                    display: 'block'
                });

                if (!this.showNotMatchLabel) {
                    this.dropdownElement.innerHTML = `<ul class='autocomplete-list-portal'>${this.filteredOptions
                        .map((opt, i) => {
                            const isHighlighted =
                                i === this.highlightedIndex ? 'is-highlighted' : '';
                            const val = opt[this.optionValue];
                            return `<li class="${isHighlighted}" data-index="${i}" title="${val}">${this.highlightMatch(
                                val
                            )}</li>`;
                        })
                        .join('')}</ul>`;

                    // Item event listeners
                    Array.from(this.dropdownElement.children[0].children).forEach(li => {
                        li.onmousedown = e => {
                            e.preventDefault();
                            this.selectOption(this.filteredOptions[li.dataset.index]);
                        };
                        // Check overflow for list items to show/hide tooltip
                        li.onmouseover = () => {
                            if (li.scrollWidth <= li.clientWidth) li.removeAttribute('title');
                        };
                    });
                } else {
                    this.dropdownElement.innerHTML = `<div class='autocomplete-not-match'><span><strong>
                        ${this.$t('Shared.SearchNotMatch')}</strong></span></div>`;
                }
            });
        },

        removeDropdown() {
            if (this.dropdownElement?.parentNode) {
                this.dropdownElement.parentNode.removeChild(this.dropdownElement);
                this.dropdownElement = null;
            }
        },

        selectOption(option, triggerNext = true) {
            const val = option[this.optionKey];
            this.$refs.inputRef.innerText = option[this.optionValue];
            this.dataSelect = {
                code: val,
                description: option[this.optionValue]
            };

            this.validateCell(val);
            this.closeDropdown();

            if (triggerNext) {
                this.$nextTick(() => this.$emit('nextCell'));
            }
        },

        // Standard methods...
        highlightMatch(text) {
            if (!this.searchQuery) {
                return text;
            }
            const regex = new RegExp(`(${this.searchQuery})`, 'gi');
            return String(text).replace(regex, '<b class="match-highlight">$1</b>');
        },
        navigateOptions(direction) {
            if (!this.showDropdown) {
                this.showDropdown = true;
                return;
            }
            const len = this.filteredOptions.length;

            if (len > 0) {
                this.highlightedIndex = (this.highlightedIndex + direction + len) % len;
            }
        },
        toggleDropdown() {
            if (!this.editableCell) {
                return;
            }

            this.showDropdown = !this.showDropdown;

            if (this.showDropdown) {
                this.$refs.inputRef.focus();
            }
        },
        requestEdit() {
            EditableCellBase.methods.requestEdit.call(this);

            this.$nextTick(() => {
                this.toggleDropdown();
            });
        }
    },
    beforeDestroy() {
        this.removeDropdown();
    },
    directives: {
        'click-outside': {
            bind(el, binding, vnode) {
                el.clickOutsideEvent = e => {
                    if (!(el === e.target || el.contains(e.target)))
                        vnode.context[binding.expression]();
                };
                document.body.addEventListener('mousedown', el.clickOutsideEvent);
            },
            unbind(el) {
                document.body.removeEventListener('mousedown', el.clickOutsideEvent);
            }
        }
    }
};
</script>

<style>
/* Portal styles (Global - reside en el body) */
.autocomplete-list-portal {
    background: white;
    border: 1px solid #ccc;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    margin: 0;
    padding: 0;
    list-style: none;
    max-height: 200px;
    overflow-y: auto;
    z-index: 999999;
}

.autocomplete-list-portal li {
    padding: 8px 12px;
    cursor: pointer;
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #3e3c4f;
    transition: all 0.3s ease-in-out;
}

.autocomplete-list-portal li .match-highlight {
    color: #19bee8;
    font-weight: bold;
    border-bottom: 2px solid #19bee8;
    transition: all 0.3s ease-in-out;
}

.autocomplete-list-portal li.is-highlighted {
    background: linear-gradient(90deg, #19bee8 0px, #19bee8 5px, #eee 4px, #fff);
    transition: all 0.3s ease-in-out;
}
.autocomplete-list-portal li.is-highlighted:hover {
    background: linear-gradient(90deg, #19bee8 0px, #19bee8 5px, #eee 6px, #fff);
    padding-left: 16px;
    color: #19bee8 !important;
}

.autocomplete-list-portal li:hover {
    background-color: #19bee8 !important;
    color: #ffffff;
}
.autocomplete-list-portal li:hover .match-highlight {
    color: #ffffff;
    border-bottom: 2px solid #fff;
}
.autocomplete-list-portal li.is-highlighted .match-highlight {
    color: #19bee8;
}
.autocomplete-list-portal li.is-highlighted:hover .match-highlight {
    color: #19bee8;
    border-bottom: 2px solid #19bee8;
}

.autocomplete-not-match {
    background: white;
    border: 1px solid #ccc;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    margin: 0;
    padding: 0;
    z-index: 999999;
}

.autocomplete-not-match span {
    padding: 8px 12px;
    cursor: auto;
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    border-bottom: 1px solid #eee;
    color: #333;
}
</style>

<style scoped>
.md-editable-grid-cell-display {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.editable-select-cell-content-wrapper {
    display: flex;
    align-items: stretch;
    width: 100%;
    height: 100%;
    position: relative;
}

.editable-select-cell-icon-container {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: #19bee8;
    cursor: pointer;
    z-index: 11;
}
tbody.md-editable-grid-body-append .editable-select-cell-icon-container {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: #19bee8;
    cursor: pointer;
    z-index: 11;
}

/* Display mode: flex-based so the icon stays within the cell scroll area */
.editable-select-cell-display-wrapper {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    overflow: hidden;
}
.editable-select-cell-display-wrapper .md-editable-grid-cell-display {
    flex: 1;
    min-width: 0;
}
.editable-select-cell-display-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding-right: 6px;
    color: #19bee8;
    cursor: pointer;
}
tbody.md-editable-grid-body-append .editable-select-cell-display-icon {
    color: #19bee8;
}
</style>
